/**
 * MedicalSafetyTab — consolidated tab with four sections:
 * Medical Profile, Allergies, Consent, Pickup Persons.
 * Each section has its own explicit Save button.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, TextField, Checkbox, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, IconButton, Alert, Divider,
  CircularProgress, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { studentAPI } from '../../services/api';
import { CONDITION_FIELDS, SEVERITY_OPTIONS } from './medicalSafetyFields';

// ---- helpers ----------------------------------------------------------------

function SectionHeader({ title, id }) {
  return (
    <Typography
      id={id}
      variant="h6"
      sx={{ fontWeight: 700, mt: 3, mb: 1.5, fontSize: '1.1rem' }}
    >
      {title}
    </Typography>
  );
}

function SaveButton({ saving, onClick, label = 'Save' }) {
  return (
    <Button
      variant="contained"
      startIcon={<SaveIcon />}
      onClick={onClick}
      disabled={saving}
      sx={{ mt: 2 }}
    >
      {saving ? 'Saving…' : label}
    </Button>
  );
}

const CONSENT_FIELDS = [
  { key: 'consent_classroom', label: 'Classroom photos/videos' },
  { key: 'consent_newsletters', label: 'Newsletters' },
  { key: 'consent_hallways', label: 'Hallway displays' },
  { key: 'consent_website', label: 'Website' },
  { key: 'consent_flyers', label: 'Flyers' },
  { key: 'consent_slideshow_building', label: 'Slideshow / building displays' },
  { key: 'consent_class_list', label: 'Class list' },
  { key: 'consent_yearbook', label: 'Yearbook' },
];

const COMM_FIELDS = [
  { key: 'comm_sms', label: 'SMS' },
  { key: 'comm_email', label: 'Email' },
  { key: 'comm_voice', label: 'Voice' },
  { key: 'comm_data_rates_acknowledged', label: 'Data rates acknowledged' },
];


export default function MedicalSafetyTab({ studentId, canEdit }) {
  // ---- state ---------------------------------------------------------------
  const [profile, setProfile] = useState(null);
  const [allergies, setAllergies] = useState([]);
  const [consent, setConsent] = useState(null);
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);
  const [savingAllergies, setSavingAllergies] = useState(false);
  const [savingPickups, setSavingPickups] = useState(false);

  // ---- load ----------------------------------------------------------------
  const loadAll = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const [profRes, allergyRes, consentRes, pickupRes] = await Promise.all([
        studentAPI.getMedicalProfile(studentId),
        studentAPI.getAllergies(studentId),
        studentAPI.getConsent(studentId),
        studentAPI.getPickupPersons(studentId),
      ]);
      setProfile(profRes.data);
      setAllergies(Array.isArray(allergyRes.data) ? allergyRes.data : []);
      setConsent(consentRes.data);
      setPickups(Array.isArray(pickupRes.data) ? pickupRes.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---- flash messages -------------------------------------------------------
  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  // ---- profile handlers -----------------------------------------------------
  const updateProfileField = (key, value) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    setError('');
    try {
      const res = await studentAPI.updateMedicalProfile(studentId, profile);
      setProfile(res.data);
      flash('Medical profile saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save medical profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  // ---- allergy handlers -----------------------------------------------------
  const updateAllergyField = (index, key, value) => {
    setAllergies((prev) => prev.map((a, i) => i === index ? { ...a, [key]: value } : a));
  };

  const addAllergy = () => {
    if (allergies.length >= 5) return;
    setAllergies((prev) => [
      ...prev,
      {
        allergen: '', severity: '', reacts_to_eat: false,
        reacts_to_touch: false, reacts_to_smell: false,
        reaction_description: '', requires_anaphylaxis_plan: false,
        order: prev.length + 1, _new: true,
      },
    ]);
  };

  const removeAllergy = async (index) => {
    const allergy = allergies[index];
    if (allergy.id) {
      try {
        await studentAPI.deleteAllergy(studentId, allergy.id);
      } catch (e) {
        setError('Failed to delete allergy.');
        return;
      }
    }
    setAllergies((prev) => prev.filter((_, i) => i !== index));
  };

  const saveAllergies = async () => {
    setSavingAllergies(true);
    setError('');
    try {
      for (let i = 0; i < allergies.length; i++) {
        const a = allergies[i];
        const payload = {
          allergen: a.allergen,
          severity: a.severity,
          reacts_to_eat: a.reacts_to_eat,
          reacts_to_touch: a.reacts_to_touch,
          reacts_to_smell: a.reacts_to_smell,
          reaction_description: a.reaction_description,
          requires_anaphylaxis_plan: !!a.requires_anaphylaxis_plan,
          order: i + 1,
        };
        if (a.id && !a._new) {
          await studentAPI.updateAllergy(studentId, a.id, payload);
        } else {
          await studentAPI.createAllergy(studentId, payload);
        }
      }
      // Reload to get IDs
      const res = await studentAPI.getAllergies(studentId);
      setAllergies(Array.isArray(res.data) ? res.data : []);
      flash('Allergies saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save allergies.');
    } finally {
      setSavingAllergies(false);
    }
  };

  // ---- consent handlers -----------------------------------------------------
  const updateConsentField = (key, value) => {
    setConsent((prev) => ({ ...prev, [key]: value }));
  };

  const saveConsent = async () => {
    setSavingConsent(true);
    setError('');
    try {
      const res = await studentAPI.updateConsent(studentId, consent);
      setConsent(res.data);
      flash('Consent preferences saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save consent.');
    } finally {
      setSavingConsent(false);
    }
  };

  // ---- pickup handlers ------------------------------------------------------
  const updatePickupField = (index, key, value) => {
    setPickups((prev) => prev.map((p, i) => i === index ? { ...p, [key]: value } : p));
  };

  const addPickup = () => {
    if (pickups.length >= 3) return;
    setPickups((prev) => [
      ...prev,
      { name: '', phone: '', order: prev.length + 1, _new: true },
    ]);
  };

  const removePickup = async (index) => {
    const p = pickups[index];
    if (p.id) {
      try {
        await studentAPI.deletePickupPerson(studentId, p.id);
      } catch (e) {
        setError('Failed to delete pickup person.');
        return;
      }
    }
    setPickups((prev) => prev.filter((_, i) => i !== index));
  };

  const savePickups = async () => {
    setSavingPickups(true);
    setError('');
    try {
      for (let i = 0; i < pickups.length; i++) {
        const p = pickups[i];
        const payload = { name: p.name, phone: p.phone, order: i + 1 };
        if (p.id && !p._new) {
          await studentAPI.updatePickupPerson(studentId, p.id, payload);
        } else {
          await studentAPI.createPickupPerson(studentId, payload);
        }
      }
      const res = await studentAPI.getPickupPersons(studentId);
      setPickups(Array.isArray(res.data) ? res.data : []);
      flash('Pickup persons saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save pickup persons.');
    } finally {
      setSavingPickups(false);
    }
  };

  // ---- render ---------------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* =============== MEDICAL PROFILE =============== */}
      <SectionHeader title="Medical Profile" id="section-medical-profile" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Conditions
        </Typography>
        <Grid container spacing={0}>
          {CONDITION_FIELDS.map((f) => (
            <Grid item xs={6} sm={4} key={f.key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!profile?.[f.key]}
                    onChange={(e) => updateProfileField(f.key, e.target.checked)}
                    disabled={!canEdit}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{f.label}</Typography>}
              />
            </Grid>
          ))}
        </Grid>

        <TextField
          label="Medications"
          fullWidth multiline rows={2} margin="normal" size="small"
          value={profile?.medications_text || ''}
          onChange={(e) => updateProfileField('medications_text', e.target.value)}
          disabled={!canEdit}
        />
        <TextField
          label="Other medical notes"
          fullWidth multiline rows={2} margin="normal" size="small"
          value={profile?.other_medical_notes || ''}
          onChange={(e) => updateProfileField('other_medical_notes', e.target.value)}
          disabled={!canEdit}
        />
        <TextField
          label="Food from home"
          fullWidth margin="normal" size="small"
          value={profile?.food_from_home || ''}
          onChange={(e) => updateProfileField('food_from_home', e.target.value)}
          disabled={!canEdit}
          helperText="e.g. peanut-free lunch from home only"
        />
        <TextField
          label="Liquid type"
          fullWidth margin="normal" size="small"
          value={profile?.liquid_type || ''}
          onChange={(e) => updateProfileField('liquid_type', e.target.value)}
          disabled={!canEdit}
          helperText="e.g. thin / nectar-thick / honey-thick"
        />

        <Divider sx={{ my: 2 }} />

        <TextField
          label="Emergency action plan"
          fullWidth multiline rows={3} margin="normal" size="small"
          value={profile?.emergency_action_plan || ''}
          onChange={(e) => updateProfileField('emergency_action_plan', e.target.value)}
          disabled={!canEdit}
          helperText="What staff should do immediately — EpiPen location, seizure protocol, inhaler use, etc."
          sx={{
            '& .MuiOutlinedInput-root': {
              borderColor: profile?.emergency_action_plan ? '#1565c0' : undefined,
            },
          }}
        />

        {canEdit && <SaveButton saving={savingProfile} onClick={saveProfile} label="Save Medical Profile" />}
      </Paper>

      {/* =============== ALLERGIES =============== */}
      <SectionHeader title="Allergies" id="section-allergies" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        {allergies.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No allergies recorded.
          </Typography>
        )}
        {allergies.map((a, idx) => (
          <Box
            key={a.id || `new-${idx}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField
                label="Allergen"
                size="small"
                value={a.allergen || ''}
                onChange={(e) => updateAllergyField(idx, 'allergen', e.target.value)}
                disabled={!canEdit}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Severity</InputLabel>
                <Select
                  label="Severity"
                  value={a.severity || ''}
                  onChange={(e) => updateAllergyField(idx, 'severity', e.target.value)}
                  disabled={!canEdit}
                >
                  {SEVERITY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {canEdit && (
                <IconButton size="small" color="error" onClick={() => removeAllergy(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={!!a.reacts_to_eat} onChange={(e) => updateAllergyField(idx, 'reacts_to_eat', e.target.checked)} disabled={!canEdit} />}
                label={<Typography variant="body2">Eat</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={!!a.reacts_to_touch} onChange={(e) => updateAllergyField(idx, 'reacts_to_touch', e.target.checked)} disabled={!canEdit} />}
                label={<Typography variant="body2">Touch</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={!!a.reacts_to_smell} onChange={(e) => updateAllergyField(idx, 'reacts_to_smell', e.target.checked)} disabled={!canEdit} />}
                label={<Typography variant="body2">Smell</Typography>}
              />
            </Box>
            <FormControlLabel
              sx={{ mb: 1, display: 'block' }}
              control={(
                <Checkbox
                  size="small"
                  checked={!!a.requires_anaphylaxis_plan}
                  onChange={(e) => updateAllergyField(idx, 'requires_anaphylaxis_plan', e.target.checked)}
                  disabled={!canEdit}
                />
              )}
              label={(
                <Typography variant="body2">
                  Food allergy / anaphylaxis risk — requires emergency plan (form_16)
                </Typography>
              )}
            />
            <TextField
              label="Reaction description"
              fullWidth size="small" multiline rows={1}
              value={a.reaction_description || ''}
              onChange={(e) => updateAllergyField(idx, 'reaction_description', e.target.value)}
              disabled={!canEdit}
            />
          </Box>
        ))}
        {canEdit && allergies.length < 5 && (
          <Button startIcon={<AddIcon />} onClick={addAllergy} size="small" sx={{ mt: 1 }}>
            Add allergy
          </Button>
        )}
        {canEdit && allergies.length > 0 && (
          <Box><SaveButton saving={savingAllergies} onClick={saveAllergies} label="Save Allergies" /></Box>
        )}
      </Paper>

      {/* =============== CONSENT =============== */}
      <SectionHeader title="Photo / Video Consent & Communication" id="section-consent" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Photo / Video Consent
        </Typography>
        <Grid container spacing={0}>
          {CONSENT_FIELDS.map((f) => (
            <Grid item xs={6} sm={4} key={f.key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!consent?.[f.key]}
                    onChange={(e) => updateConsentField(f.key, e.target.checked)}
                    disabled={!canEdit}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{f.label}</Typography>}
              />
            </Grid>
          ))}
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
          Communication Preferences
        </Typography>
        <Grid container spacing={0}>
          {COMM_FIELDS.map((f) => (
            <Grid item xs={6} sm={4} key={f.key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!consent?.[f.key]}
                    onChange={(e) => updateConsentField(f.key, e.target.checked)}
                    disabled={!canEdit}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{f.label}</Typography>}
              />
            </Grid>
          ))}
        </Grid>

        {canEdit && <SaveButton saving={savingConsent} onClick={saveConsent} label="Save Consent & Communication" />}
      </Paper>

      {/* =============== PICKUP PERSONS =============== */}
      <SectionHeader title="Authorized Pickup Persons" id="section-pickup" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        {pickups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No pickup persons recorded.
          </Typography>
        )}
        {pickups.map((p, idx) => (
          <Box
            key={p.id || `new-${idx}`}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ minWidth: 20, fontWeight: 600 }}>
              {idx + 1}.
            </Typography>
            <TextField
              label="Name"
              size="small"
              value={p.name || ''}
              onChange={(e) => updatePickupField(idx, 'name', e.target.value)}
              disabled={!canEdit}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Phone"
              size="small"
              value={p.phone || ''}
              onChange={(e) => updatePickupField(idx, 'phone', e.target.value)}
              disabled={!canEdit}
              sx={{ width: 180 }}
            />
            {canEdit && (
              <IconButton size="small" color="error" onClick={() => removePickup(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        {canEdit && pickups.length < 3 && (
          <Button startIcon={<AddIcon />} onClick={addPickup} size="small" sx={{ mt: 1 }}>
            Add pickup person
          </Button>
        )}
        {canEdit && pickups.length > 0 && (
          <Box><SaveButton saving={savingPickups} onClick={savePickups} label="Save Pickup Persons" /></Box>
        )}
      </Paper>
    </Box>
  );
}
